/**
 * `<InlineCodePicker>` — Combobox-based code picker for table cells in the
 * Progress Note edit form (Problem List, Orders). Mockup parity: the visible
 * input shows ONLY the code in coral monospace; the dropdown items show
 * `code  description`; searching matches either field. Description writes
 * to a sibling column via `onChange`, not into the input itself.
 *
 * Used in two modes:
 *
 *   1. Eager mode (CPT in Orders) — caller passes the full `options` list
 *      up front; the picker filters client-side by the typed query.
 *
 *   2. Live mode (ICD-10 in Assessment) — caller wires `onSearchChange` and
 *      fetches matching codes externally (e.g. ValueSet expansion), then
 *      passes the updated `options` prop back. The picker still filters
 *      client-side as a sanity layer but trusts the caller to populate
 *      `options` with the relevant subset for the current query.
 *
 * Why a custom component (not Mantine `Select`):
 *  - Mantine `Select` renders the selected option's `label` verbatim as the
 *    input's display value. We need the input to show only the `code`, while
 *    the dropdown shows `code  description` AND the search filters on both
 *    fields. The label/value duality of `Select` can't do that without
 *    awkward overrides.
 *  - `Combobox` + `InputBase` is the documented Mantine v8 pattern for full
 *    control over input display vs option rendering vs search semantics.
 */
import { useEffect, useMemo, useState } from 'react'
import { Combobox, InputBase, Loader, ScrollArea, Text, useCombobox } from '@mantine/core'
import { CLEAR_OPTION_VALUE, ClearSelectionOption } from './ClearSelectionOption'
import { MONO_FONT, PROGRESS_NOTE_CORAL as CORAL } from './tokens'

export type InlineCodeOption = {
  code: string
  display: string
  system: string
}

export interface InlineCodePickerProps {
  /** Currently selected code+display. Empty value renders an empty input. */
  value?: { code: string; display?: string; system?: string }
  /** Candidate options shown in the dropdown. Filtered client-side by `searchText`. */
  options: InlineCodeOption[]
  /** Fired on pick. Receives both the code and the display so the consumer can
   *  write each to a separate column. Also receives `system` for FHIR persistence. */
  onChange: (picked: InlineCodeOption) => void
  /** Optional — fires whenever the user types. Useful for live-fetched
   *  ValueSets where the consumer is responsible for refreshing `options`. */
  onSearchChange?: (query: string) => void
  loading?: boolean
  placeholder?: string
  testId?: string
}

export function InlineCodePicker({
  value,
  options,
  onChange,
  onSearchChange,
  loading,
  placeholder,
  testId,
}: InlineCodePickerProps) {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  })

  // The visible input value mirrors the row's `code`. When the doctor types,
  // we treat it as a search query — the actual row value only changes when
  // they pick from the dropdown.
  const [search, setSearch] = useState<string>(value?.code ?? '')

  // Resync when the external value changes (e.g. AI hydrate, programmatic set).
  useEffect(() => {
    setSearch(value?.code ?? '')
  }, [value?.code])

  // Filter client-side by code OR description match. In live mode the consumer
  // is expected to populate `options` with the query-relevant subset, but
  // double-filtering here keeps the dropdown crisp if a stale fetch returns
  // late.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return options.slice(0, 30)
    return options
      .filter(
        (o) => o.code.toLowerCase().includes(q) || (o.display?.toLowerCase().includes(q) ?? false),
      )
      .slice(0, 30)
  }, [options, search])

  return (
    <Combobox
      store={combobox}
      withinPortal
      width={380}
      position="bottom-start"
      onOptionSubmit={(submittedCode) => {
        if (submittedCode === CLEAR_OPTION_VALUE) {
          // Empty option = clear; consumers treat an empty code/display as "no code".
          onChange({ code: '', display: '', system: '' })
          setSearch('')
          combobox.closeDropdown()
          return
        }
        const picked = options.find((o) => o.code === submittedCode)
        if (picked) {
          onChange(picked)
          setSearch(picked.code)
        }
        combobox.closeDropdown()
      }}
    >
      <Combobox.Target>
        <InputBase
          size="xs"
          value={search}
          onChange={(event) => {
            const next = event.currentTarget.value
            setSearch(next)
            onSearchChange?.(next)
            combobox.openDropdown()
            combobox.updateSelectedOptionIndex()
          }}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => combobox.closeDropdown()}
          placeholder={placeholder}
          rightSection={loading ? <Loader size={12} /> : <Combobox.Chevron />}
          rightSectionPointerEvents="none"
          styles={{
            input: {
              fontFamily: MONO_FONT,
              color: search ? CORAL : undefined,
              fontWeight: 600,
            },
          }}
          data-testid={testId}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          {value?.code && <ClearSelectionOption />}
          <ScrollArea.Autosize mah={280} type="scroll">
            {filtered.length === 0 ? (
              <Combobox.Empty>{loading ? 'Searching…' : 'No matches'}</Combobox.Empty>
            ) : (
              filtered.map((o) => (
                <Combobox.Option value={o.code} key={`${o.system}|${o.code}`}>
                  <Text component="span" ff={MONO_FONT} fw={700} mr="sm" style={{ color: CORAL }}>
                    {o.code}
                  </Text>
                  <Text component="span" c="dimmed" size="sm">
                    {o.display}
                  </Text>
                </Combobox.Option>
              ))
            )}
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}
